/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useServiceContext } from '../root'

interface ChecksumData {
  fileName: string
  checksum: string
  existFiles: any[] | null
}

interface ChecksumValidationData {
  fileName: string
  checksumValidate: string | true
}

type ChecksumResult = ChecksumData | ChecksumValidationData

export function useChecksumService() {
  const context = useServiceContext()

  /**
   * Calculate SHA1 checksums for multiple files and check for duplicates
   * @param files - Array of files to process
   * @param dataFileSizeLimit - Maximum file size allowed in bytes
   * @returns Array of checksum data or validation errors
   */
  async function sha1_hash(files: File[], dataFileSizeLimit: number): Promise<ChecksumResult[]> {
    const addedChecksum: ChecksumResult[] = []

    const processFile = async (fileItem: File) => {
      const valFile = validateFile(fileItem, dataFileSizeLimit)
      const valData: ChecksumValidationData = {
        fileName: fileItem.name,
        checksumValidate: valFile
      }

      if (valFile === true) {
        try {
          const data = await calculateChecksum(fileItem)
          addedChecksum.push(data)

          // Check if file with this checksum already exists
          const dataRes = await context.api.files.getCalculatedChecksum(data.checksum)
          if (dataRes.elements) {
            const matchedElements = dataRes.elements.filter((el: any) => el.checksum === data.checksum)
            if (matchedElements.length > 0) {
              data.existFiles = matchedElements
            }
          }
        } catch (error) {
          console.error('Error calculating checksum:', error)
        }
      } else {
        addedChecksum.push(valData)
      }
    }

    // Process all files in parallel
    await Promise.all(files.map((file) => processFile(file)))

    return addedChecksum
  }

  /**
   * Validate file type and size
   * @param file - File to validate
   * @param dataFileSizeLimit - Maximum file size in bytes
   * @returns true if valid, error message if invalid
   */
  function validateFile(file: File, dataFileSizeLimit: number): string | true {
    const fileExe = isFileTypeValid(file)
    const fileSize = isFileSizeExceed(file, dataFileSizeLimit)

    if (fileExe !== true) {
      return fileExe
    } else if (fileSize !== true) {
      return fileSize
    } else {
      return true
    }
  }

  /**
   * Check if file type is valid (not .sh or .exe)
   * @param file - File to check
   * @returns true if valid, error message if invalid
   */
  function isFileTypeValid(file: File): string | true {
    const extension = file.name.split('.').pop()
    if (extension && ['SH', 'EXE'].includes(extension.toUpperCase())) {
      return context.service.filter.translate('FILE_TYPE_NOT_SUPPORTING', {
        extension: extension
      })
    }
    return true
  }

  /**
   * Check if file size exceeds the limit
   * @param file - File to check
   * @param dataFileSizeLimit - Maximum file size in bytes
   * @returns true if valid, error message if exceeded
   */
  function isFileSizeExceed(file: File, dataFileSizeLimit: number): string | true {
    const uploadFileSize = formatBytes(file.size)
    if (file.size > dataFileSizeLimit) {
      return context.service.filter.translate('File_SIZE_EXCEED', {
        fileSize: uploadFileSize
      })
    }
    return true
  }

  /**
   * Format bytes to human-readable string
   * @param bytes - Number of bytes
   * @param decimals - Number of decimal places
   * @returns Formatted string (e.g., "1.5 MB")
   */
  function formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  }

  /**
   * Calculate SHA1 checksum for a file using Web Crypto API
   * @param file - File to process
   * @returns Promise resolving to checksum data
   */
  function calculateChecksum(file: File): Promise<ChecksumData> {
    return new Promise((resolve, reject) => {
      if (!file) {
        return reject(new Error('No file provided'))
      }

      const reader = new FileReader()

      reader.onload = async function () {
        try {
          const arrayBuffer = reader.result as ArrayBuffer

          // Use Web Crypto API for SHA-1 hash
          const hashBuffer = await crypto.subtle.digest('SHA-1', arrayBuffer)

          // Convert ArrayBuffer to hex string
          const hashArray = Array.from(new Uint8Array(hashBuffer))
          const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')

          const data: ChecksumData = {
            fileName: file.name,
            checksum: hashHex,
            existFiles: null
          }

          resolve(data)
        } catch (err) {
          reject(err)
        }
      }

      reader.onerror = function (e) {
        reject(e)
      }

      reader.readAsArrayBuffer(file)
    })
  }

  /**
   * Get file size limit from backend
   * @returns Promise resolving to file size limit in bytes
   */
  async function getFileSizeLimit(): Promise<number> {
    try {
      const dataRes = await context.api.files.getFileSizeLimitToCalculateChecksum()
      return dataRes
    } catch (err) {
      console.error('Error getting file size limit from backend:', err)
      throw err
    }
  }

  return {
    sha1_hash,
    validateFile,
    isFileTypeValid,
    isFileSizeExceed,
    calculateChecksum,
    getFileSizeLimit,
    formatBytes
  }
}

export default useChecksumService
